import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { useMarketingPageView } from "@/hooks/use-marketing-page-view";
import { useMarketingVariantAssignment } from "@/lib/marketing-experiments";
import { homeMarketingPageConfig } from "@/lib/marketing-page-configs";
import { MarketingPageRenderer } from "@/lib/marketing-page-renderer";

const Index = () => {
  const assignment = useMarketingVariantAssignment("home");
  useMarketingPageView("home", assignment);

  return (
    <>
      <Helmet>
        <title>Lint — Sell Courses, Build Communities, Get Paid Fairly</title>
        <meta 
          name="description" 
          content="A creator platform built around transparent payouts, fair fees, and modern community tools. Sell courses, downloads, and coaching sessions." 
        />
        <link rel="canonical" href="https://www.uselint.com/" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Lint — Sell Courses, Build Communities, Get Paid Fairly" />
        <meta 
          property="og:description" 
          content="A creator platform built around transparent payouts, fair fees, and modern community tools." 
        />
        <meta property="og:image" content="https://assets.uselint.com/core/image.webp" />
        <meta property="og:url" content="https://www.uselint.com/" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lint — Sell Courses, Build Communities, Get Paid Fairly" />
        <meta 
          name="twitter:description" 
          content="A creator platform built around transparent payouts, fair fees, and modern community tools." 
        />
        <meta name="twitter:image" content="https://assets.uselint.com/core/image.webp" />
        
        {/* FAQPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Lint?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Lint is a creator platform that lets you sell courses, digital downloads, and coaching sessions. We focus on transparent pricing, fast payouts, and built-in community features — everything you need to build a sustainable creator business."
                }
              },
              {
                "@type": "Question",
                "name": "How much does it cost to get started?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can start for free! There's no monthly fee. We only take a 5% transaction fee when you make a sale (plus Stripe processing fees)."
                }
              },
              {
                "@type": "Question",
                "name": "How do payouts work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We use Stripe Connect for payments. Once you connect your Stripe account, you'll receive automatic payouts on a weekly basis. Payouts typically arrive within 2-3 business days after processing."
                }
              },
              {
                "@type": "Question",
                "name": "Can I migrate my existing courses?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! You can import your existing content into Lint. Our course builder makes it easy to upload videos, create lessons, and organize your curriculum. If you need help migrating, our support team is here to assist."
                }
              },
              {
                "@type": "Question",
                "name": "What payment methods do you support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Through Stripe, we support all major credit and debit cards, Apple Pay, Google Pay, and many local payment methods depending on your customers' location. We handle all the payment processing so you can focus on creating."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer refunds?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You control your refund policy. When you issue a refund, the platform fee is also refunded to you."
                }
              },
              {
                "@type": "Question",
                "name": "How does the creator referral program work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Invite other creators to Lint. When they publish and make their first sale, you earn fee reductions. Each activated referral earns -1% platform fee for 6 months. Stack up to 2% off (down to 3% platform fee)."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        <main>
          <MarketingPageRenderer
            config={homeMarketingPageConfig}
            experimentId={assignment.experimentId}
            variant={assignment.variantConfig}
            variantId={assignment.variantId}
          />
        </main>
        <LandingFooter />
      </div>
    </>
  );
};

export default Index;
